<script setup lang="ts">
  import { ref, onMounted, computed } from "vue";
  import { useRouter } from "vue-router";
  import { useOfferteBundels } from "../composables/useOfferteBundels";
  import CustomTag from "./CustomTag.vue";
  import Button from "primevue/button";
  import ProgressSpinner from "primevue/progressspinner";
  import Message from "primevue/message";

  const props = defineProps<{
    opdrachtgeverId: string;
  }>();

  const emit = defineEmits<{
    (e: "bundel-signed", bundelId: string): void;
  }>();

  const router = useRouter();
  const { bundels, loading, error, fetchBundels, getPendingBundels, getSignedBundels } = useOfferteBundels(
    props.opdrachtgeverId,
  );

  const expandedBundels = ref<Set<string>>(new Set());

  onMounted(async () => {
    await fetchBundels();
    // Auto-expand pending bundels
    for (const b of getPendingBundels()) {
      expandedBundels.value.add(b.id);
    }
  });

  const pendingBundels = computed(() => getPendingBundels());
  const signedBundels = computed(() => getSignedBundels());
  const sortedBundels = computed(() => [...pendingBundels.value, ...signedBundels.value]);

  function toggleExpanded(id: string) {
    const next = new Set(expandedBundels.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    expandedBundels.value = next;
  }

  function isSigned(bundel: any): boolean {
    const statuses = bundel.status || [];
    return statuses.some((s: any) => s.status === "alles_geaccepteerd");
  }

  function getStatusColor(bundel: any): string {
    return isSigned(bundel) ? "alles_geaccepteerd" : "verzonden";
  }

  function getStatusLabel(bundel: any): string {
    return isSigned(bundel) ? "Geaccepteerd" : "Open";
  }

  function formatCurrency(amount: number | null): string {
    if (amount === null || amount === undefined) return "€ 0,00";
    return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(amount);
  }

  function formatDate(date: string | null): string {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("nl-NL");
  }

  function getOfferteLatestStatus(offerte: any): string {
    if (!offerte?.status || !Array.isArray(offerte.status) || offerte.status.length === 0) return "nieuw";
    return offerte.status[offerte.status.length - 1]?.status || "nieuw";
  }

  function getOfferteStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      nieuw: "Nieuw",
      aangepast: "Nieuw",
      verzonden: "Nieuw",
      bekeken: "Nieuw",
      deels_geaccepteerd: "Deels Geaccepteerd",
      alles_geaccepteerd: "Geaccepteerd",
      foute_inzending: "Foute inzending",
      ingepland: "Ingepland",
      ingetrokken: "Ingetrokken",
      afgewezen: "Afgewezen",
    };
    return labels[status] || status;
  }

  function calculateBundelTotal(bundel: any): number {
    if (!bundel.offertes || bundel.offertes.length === 0) return 0;
    return bundel.offertes.reduce((sum: number, offerte: any) => {
      return sum + (offerte.totaalIncKorting || 0);
    }, 0);
  }
</script>

<template>
  <div class="bundel-overview">
    <div v-if="loading && !bundels.length" class="flex justify-center py-8">
      <ProgressSpinner style="width: 40px; height: 40px" />
    </div>

    <Message v-else-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div v-else-if="bundels.length === 0" class="status-empty">
      <i class="pi pi-inbox text-background-300 mb-3 text-4xl"></i>
      <p>Geen offerte bundels gevonden</p>
    </div>

    <div v-else>
      <div class="bundel-toolbar">
        <span class="bundel-count">{{ sortedBundels.length }} bundels</span>
        <Button
          icon="pi pi-refresh"
          @click="fetchBundels()"
          :loading="loading"
          severity="secondary"
          outlined
          size="small"
          v-tooltip="'Vernieuwen'"
        />
      </div>

      <div class="bundel-list">
        <div v-for="bundel in sortedBundels" :key="bundel.id" class="bundel-item">
          <div
            class="bundel-row"
            :class="isSigned(bundel) ? 'is-disabled' : 'is-active'"
            tabindex="0"
            role="button"
            :aria-expanded="expandedBundels.has(bundel.id)"
            @click="toggleExpanded(bundel.id)"
            @keydown.enter="toggleExpanded(bundel.id)"
            @keydown.space.prevent="toggleExpanded(bundel.id)"
          >
            <i
              class="pi text-background-500 shrink-0 text-sm transition-transform"
              :class="expandedBundels.has(bundel.id) ? 'pi-chevron-down' : 'pi-chevron-right'"
            />

            <div class="bundel-row-main">
              <div class="bundel-row-title">
                <span class="bundel-title">{{ bundel.naam || "Bundel" }}</span>
                <span class="bundel-nr">{{ bundel.bundel_nr }}</span>
              </div>
              <span v-if="bundel.omschrijving" class="bundel-subtitle">{{ bundel.omschrijving }}</span>
            </div>

            <span class="bundel-offerte-count">
              {{ bundel.offertes?.length || 0 }}
            </span>

            <div class="bundel-row-status" @click.stop>
              <CustomTag :color="getStatusColor(bundel)" :label="getStatusLabel(bundel)" />
            </div>

            <div class="bundel-row-price">
              <span class="price-value">{{ formatCurrency(calculateBundelTotal(bundel)) }}</span>
            </div>
          </div>

          <Transition name="expand">
            <div v-if="expandedBundels.has(bundel.id)" class="bundel-details">
              <!-- Nested offerte rows -->
              <div class="offerte-sublist">
                <div
                  v-for="offerte in bundel.offertes"
                  :key="offerte.id"
                  class="offerte-subrow"
                  @click="router.push({ name: 'OfferteDetails', params: { id: offerte.id } })"
                >
                  <span class="offerte-subrow-nr">{{ offerte.offerteNr || "Offerte" }}</span>
                  <span class="offerte-subrow-date">{{ formatDate(offerte.offerteDatum) }}</span>
                  <CustomTag
                    :color="getOfferteLatestStatus(offerte)"
                    :label="getOfferteStatusLabel(getOfferteLatestStatus(offerte))"
                  />
                  <span class="offerte-subrow-price">
                    {{ formatCurrency(offerte.totaalIncKorting || 0) }}
                  </span>
                  <i class="pi pi-arrow-right text-background-400 text-xs"></i>
                </div>
              </div>

              <!-- Footer -->
              <div class="bundel-details-footer">
                <div class="bundel-total-line">
                  <span class="text-background-600 text-sm font-medium">Totaal</span>
                  <span class="text-background-900 font-mono text-base font-semibold">
                    {{ formatCurrency(calculateBundelTotal(bundel)) }}
                  </span>
                </div>
                <Button
                  label="Bekijken"
                  :icon="isSigned(bundel) ? 'pi pi-eye' : 'pi pi-pen-to-square'"
                  :severity="isSigned(bundel) ? 'secondary' : 'success'"
                  size="large"
                  @click="router.push({ name: 'BundelDetails', params: { id: bundel.id } })"
                />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .status-empty {
    text-align: center;
    padding: 2rem 0;
    color: var(--p-background-500);
  }

  .bundel-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--p-background-200);
  }

  .bundel-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-600);
  }

  .bundel-list {
    display: flex;
    flex-direction: column;
    padding-top: 0.5rem;
  }

  .bundel-item {
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--p-background-200);
  }

  .bundel-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto auto;
    gap: 1rem;
    align-items: center;
    padding: 0.25rem 0;
    cursor: pointer;
  }

  .bundel-row:focus-visible {
    outline: 2px solid var(--p-primary-200);
    outline-offset: 3px;
  }

  .bundel-row.is-disabled {
    opacity: 0.6;
  }

  .bundel-row.is-active:hover {
    border-color: var(--p-primary-200);
  }

  .bundel-row-main {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .bundel-row-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .bundel-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--p-background-900);
  }

  .bundel-nr {
    font-size: 0.75rem;
    color: var(--p-background-500);
    font-family: monospace;
  }

  .bundel-subtitle {
    font-size: 0.8rem;
    color: var(--p-background-600);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bundel-offerte-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: var(--p-background-100);
    color: var(--p-background-700);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .bundel-row-status,
  .bundel-row-price {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .price-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-900);
  }

  .bundel-details {
    padding: 0.75rem 0 0.25rem;
    border-top: 1px solid var(--p-background-100);
  }

  .offerte-sublist {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-left: 1.25rem;
  }

  .offerte-subrow {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto auto;
    gap: 0.75rem;
    align-items: center;
    padding: 0.5rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .offerte-subrow:hover {
    background: var(--p-background-50);
  }

  .offerte-subrow-nr {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-800);
  }

  .offerte-subrow-date {
    font-size: 0.8rem;
    color: var(--p-background-500);
  }

  .offerte-subrow-price {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--p-background-800);
    font-family: monospace;
  }

  .bundel-details-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-background-200);
    margin-left: 1.25rem;
  }

  .bundel-total-line {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Expand transition */
  .expand-enter-active,
  .expand-leave-active {
    transition: all 0.2s ease-out;
    overflow: hidden;
  }

  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  .expand-enter-to,
  .expand-leave-from {
    opacity: 1;
    max-height: 500px;
  }

  /* Mobile */
  @media (max-width: 640px) {
    .bundel-row {
      grid-template-columns: auto 1fr auto;
      grid-template-areas:
        "chevron main status"
        "chevron price price";
      row-gap: 0.25rem;
    }

    .bundel-row > i.pi {
      grid-area: chevron;
      align-self: center;
    }

    .bundel-row-main {
      grid-area: main;
    }

    .bundel-offerte-count {
      display: none;
    }

    .bundel-row-status {
      grid-area: status;
      justify-content: flex-end;
      align-items: flex-start;
    }

    .bundel-row-price {
      grid-area: price;
      justify-content: flex-start;
    }

    .price-value {
      font-size: 0.9rem;
      color: var(--p-background-600);
      font-weight: 500;
    }

    /* offerte subrow: hide date, hide arrow — show nr | tag | price */
    .offerte-sublist {
      margin-left: 0;
    }

    .offerte-subrow {
      grid-template-columns: minmax(0, 1fr) auto auto;
    }

    .offerte-subrow-date {
      display: none;
    }

    .offerte-subrow > i.pi-arrow-right {
      display: none;
    }

    .bundel-details-footer {
      flex-direction: column;
      gap: 0.75rem;
      align-items: stretch;
      margin-left: 0;
    }

    .bundel-total-line {
      justify-content: space-between;
    }
  }
</style>
